import { Frame } from 'dragonmouse-shared/interfaces';
import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getFrameDisplayBounds } from 'redux/db/util';
import { selectTransform } from 'redux/mode/reducers';
import { getCanvasRect } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

interface Props {
	frame: Frame;
}
const FrameControls = ({ frame }: Props) => {
	const { transform } = useSelector(
		(r: RootReducer) => ({
			transform: selectTransform(r.mode),
		}),
		shallowEqual
	);

	// Mask geometry reads window.inner{Width,Height}, so we have to re-render
	// on resize — otherwise the masks bake the initial viewport size and leak
	// past the frame edges after the user resizes.
	const [viewport, setViewport] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});
	useEffect(() => {
		const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	const bounds = useMemo(() => {
		const bounds = getFrameDisplayBounds(frame);
		return getCanvasRect(bounds);
	}, [frame, transform]);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------
	const getMaskStyles = () => {
		const canvasWidth = viewport.width;
		const canvasHeight = viewport.height;

		return {
			top: {
				left: 0,
				top: 0,
				width: canvasWidth,
				height: Math.max(0, bounds.y),
			},
			bottom: {
				left: 0,
				top: bounds.y + bounds.height,
				width: canvasWidth,
				height: Math.max(0, canvasHeight - (bounds.y + bounds.height)),
			},
			left: {
				left: 0,
				top: bounds.y,
				width: Math.max(0, bounds.x),
				height: bounds.height,
			},
			right: {
				left: bounds.x + bounds.width,
				top: bounds.y,
				width: Math.max(0, canvasWidth - (bounds.x + bounds.width)),
				height: bounds.height,
			},
		};
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const maskStyles = getMaskStyles();

	return (
		<React.Fragment>
			<S.Mask style={maskStyles.top} />
			<S.Mask style={maskStyles.bottom} />
			<S.Mask style={maskStyles.left} />
			<S.Mask style={maskStyles.right} />
		</React.Fragment>
	);
};

export default FrameControls;
